import { getUnavailableVehicleDataV2 } from "api/vehicles/vehicle";
import { useData } from "data/data-provider";
import moment from "moment";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { changeDate } from "utils/changeDate";
import ClearDataButton from "../ClearDataButton";
import DatePickerCustomDay from "../DatePickerCustomDay";

// Types
import {
  DatePickerType,
  RentalCarDatesRangeInputProps,
  TabType,
  UnavailabilityData,
} from "./types/rentalCarDatesRangeInput.types";

// Utils
import {
  createDateWithoutTimezone,
  formatDate,
  formatTime,
  parsePartialUnavailability,
} from "./utils/rentalCarDatesRangeInput.utils";
import {
  getAllWeeksForSelection,
  getSelectedWeeksDateRange,
  getWeeksInMonth,
  weekContainsUnavailableDates,
} from "./utils/weeksHelper";

// Hooks
import { useClickOutside } from "./hooks/useClickOutside";
import { useDateAvailability } from "./hooks/useDateAvailability";
import { useDialInteraction } from "./hooks/useDialInteraction";
import { useScreenSize } from "./hooks/useScreenSize";

// Components
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useLocation, useNavigate } from "react-router";
import DatesView from "./components/DatesView";
import MonthsView from "./components/MonthsView";
import TabSwitcher from "./components/TabSwitcher";
import WeeksView from "./components/WeeksView";
import RentalCarTimesRangeInput from "./RentalCarTimesRangeInput";

const EndDateTimeRange: FC<RentalCarDatesRangeInputProps> = ({
  className = "",
  fieldClassName = "[ nc-hero-field-padding ]",
  selectedCar,
  isMiddle,
  bookingDetails,
  hasButtonSubmit = true,
  design,
  dropOffLocationType,
}) => {
  const {
    loadingAvailableFleet,
    ourCompany,
    s_Time,
    e_Time,
    startDate,
    endDate,
    startTime,
    endTime,
    dropOffLocation,
    dropoffLocationType,
    setStartDate,
    setEndDate,
    setStartTime,
    setEndTime,
    callAvailableFleetAPI,
    setPartiallyAvailableStartDate,
    setPartiallyAvailableEndDate,
    setPartiallyUnavailableTimes,
    partiallyUnavailableTimes,
    pickupLocation,
    setLongTermRentalDuration,
    activeTab,
    setActiveTab,
    selectedWeeks,
    setSelectedMonth,
    setSelectedWeeks,
    selectedMonth,
    monthStartDate,
    monthEndDate,
    setMonthEndDate,
    setMonthStartDate,
    setLongTermRentalNature,
    weeksViewMonth,
  } = useData();

  // Operational-hour-aware defaults: prefer the location's operational window,
  // fall back to company-level defaults (07:00 / 19:00).
  const effectiveDropoffLocation =
    dropoffLocationType === false && dropOffLocation ? dropOffLocation : pickupLocation;
  const opStartTime = pickupLocation?.operational_start_time ?? s_Time;
  const opEndTime = effectiveDropoffLocation?.operational_end_time ?? e_Time;

  const location = useLocation();
  const navigate = useNavigate();

  const [fullyUnavailableDates, setFullyUnavailableDates] = useState<Date[]>(
    [],
  );
  const [partialUnavailability, setPartialUnavailability] = useState<{
    [key: string]: { from: string; to: string }[];
  }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [calendarViewDate, setCalendarViewDate] = useState<Date | null>(null);
  const panelRef = useRef<HTMLDivElement>(null); // <-- Custom panel ref

  // Set activeTab based on bookingDetails.long_term_rental_nature
  useEffect(() => {
    if (bookingDetails?.long_term_rental_nature === "monthly") {
      setActiveTab("months");
    } else if (bookingDetails?.long_term_rental_nature === "weekly") {
      setActiveTab("weeks");
    } else if (bookingDetails && !bookingDetails.is_long_term_rental) {
      setActiveTab("dates");
    }
  }, [bookingDetails?.long_term_rental_nature, bookingDetails?.is_long_term_rental]);
  
  // Initialize dates and times from bookingDetails
  useEffect(() => {
    if (bookingDetails?.pick_up_time && bookingDetails?.drop_off_time) {
      const pickUpDateTime = new Date(bookingDetails.pick_up_time);
      const dropOffDateTime = new Date(bookingDetails.drop_off_time);
      
      // Set dates
      setStartDate(pickUpDateTime);
      setEndDate(dropOffDateTime);
      
      // Set times
      const pickUpTime = moment(pickUpDateTime).format('HH:mm');
      const dropOffTime = moment(dropOffDateTime).format('HH:mm');
      setEndTime(dropOffTime);
      setTempEndTime(dropOffTime);
      
      // For monthly rentals, set month dates
      if (bookingDetails.long_term_rental_nature === "monthly") {
        setMonthStartDate(pickUpDateTime);
        setMonthEndDate(dropOffDateTime);
        
        // Calculate months
        const start = moment(pickUpDateTime);
        const end = moment(dropOffDateTime);
        const monthsDiff = end.diff(start, 'months', true);
        const calculatedMonths = Math.max(1, Math.ceil(monthsDiff));
        setSelectedMonth(calculatedMonths);
      }
      
      // For weekly rentals, calculate weeks
      if (bookingDetails.long_term_rental_nature === "weekly") {
        const start = moment(pickUpDateTime).startOf('isoWeek');
        const end = moment(dropOffDateTime).startOf('day');
        const weekStrings: string[] = [];
        const current = start.clone();
        while (current.isSameOrBefore(end)) {
          weekStrings.push(current.format('YYYY-MM-DD'));
          current.add(1, 'week');
        }
        setSelectedWeeks(weekStrings);
      }
    }
  }, [bookingDetails?.pick_up_time, bookingDetails?.drop_off_time, bookingDetails?.long_term_rental_nature]);
  const [isPanelOpen, setIsPanelOpen] = useState(false); // <-- Panel open state

  const [showDatePicker, setShowDatePicker] = useState<DatePickerType>(null);
  const [tempSelectedDate, setTempSelectedDate] = useState<Date | null>(null);
  
  // Temporary state for date selection in dates tab
  const [tempStartDate, setTempStartDate] = useState<Date | null>(null);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(null);

  // End time state (renamed from startTime to endTime)
  const [showEndTimePicker, setShowEndTimePicker] = useState(false);
  const [tempEndTime, setTempEndTime] = useState<string>(endTime);
  const endTimePickerRef = useRef<HTMLDivElement>(null);

  // Refs for circular dial
  const dialRef = useRef<HTMLDivElement>(null);
  const isInitialMount = useRef(true);

  // Custom hooks
  const { isLargeScreen } = useScreenSize();
  const monthsToShow = isLargeScreen ? 2 : 1;

  const { isDragging, handleMouseDown, handleTouchStart } = useDialInteraction(
    dialRef,
    setSelectedMonth,
  );

  const { isDateSelectable, isRangeAvailable, isPartialOverlapValid } =
    useDateAvailability(fullyUnavailableDates, partialUnavailability);

  // Click outside for end time picker
  useClickOutside(endTimePickerRef, showEndTimePicker, () =>
    setShowEndTimePicker(false),
  );
  // Click outside for custom panel (calendar)
  useClickOutside(panelRef, isPanelOpen && !showDatePicker, () => setIsPanelOpen(false));

  // Initialize temp dates when panel opens or when activeTab is "dates"
  useEffect(() => {
    if (isPanelOpen && activeTab === "dates") {
      setTempStartDate(startDate);
      setTempEndDate(endDate);
    }
  }, [isPanelOpen, activeTab]);

  // Update end date when selected month changes
  useEffect(() => {
    const newEndDate = moment(monthStartDate)
      .add(selectedMonth * 30 - 1, "days")
      .toDate();
    setMonthEndDate(newEndDate);
  }, [selectedMonth, monthStartDate]);

  // Calculate months from date range
  const calculateMonthsFromDates = useCallback((start: Date, end: Date) => {
    const days = moment(end).diff(moment(start), "days") + 1;
    const months = Math.round(days / 30);
    return Math.max(1, Math.min(12, months));
  }, []);

  // Open date picker modal
  const openDatePickerModal = (type: DatePickerType) => {
    setShowDatePicker(type);
    setTempSelectedDate(type === "start" ? monthStartDate : monthEndDate);
  };

  // Handle date selection in modal (just temp select, not confirmed yet)
  const handleModalDateChange = (date: Date | null) => {
    if (date) {
      setTempSelectedDate(date);
    }
  };

  // Save the selected date from modal
  const handleModalSave = () => {
    if (!tempSelectedDate || !showDatePicker) return;
    if (showDatePicker === "start") {
      setMonthStartDate(tempSelectedDate);
      const months = calculateMonthsFromDates(tempSelectedDate, monthEndDate);
      setSelectedMonth(months);
    } else if (showDatePicker === "end") {
      setMonthEndDate(tempSelectedDate);
      const months = calculateMonthsFromDates(monthStartDate, tempSelectedDate);
      setSelectedMonth(months);
    }
    setShowDatePicker(null);
    setTempSelectedDate(null);
  };

  // Close modal without saving
  const handleModalClose = () => {
    setShowDatePicker(null);
    setTempSelectedDate(null);
  };

  const fetchUnavailableDates = async (startDate: Date, endDate: Date) => {
    if (!selectedCar?.id) {
      console.warn("No car selected, unable to fetch unavailable dates.");
      return;
    }

    setIsLoading(true);
    try {
      const startDateString = new Date(startDate);
      startDateString.setMonth(startDateString.getMonth() - 1);
      const endDateString = new Date(endDate);
      endDateString.setMonth(endDateString.getMonth() + 1);

      const startDateStr = formatDate(startDateString);
      const endDateStr = formatDate(endDateString);

      const response = await getUnavailableVehicleDataV2(
        {
          start_date: startDateStr,
          end_date: endDateStr,
          booking_to_exclude: bookingDetails?.id,
        },
        selectedCar.id,
        pickupLocation?.id,
      );

      if (response.data) {
        const unavailableData = response.data as UnavailabilityData;
        const fullyUnavailable = unavailableData.full_unavailable.map(
          (dateStr: any) => createDateWithoutTimezone(dateStr),
        );
        setFullyUnavailableDates(fullyUnavailable);
        const partialUnavailable = parsePartialUnavailability(
          unavailableData.partial_unavailable,
        );
        setPartialUnavailability(partialUnavailable);
        if (setPartiallyUnavailableTimes) {
          setPartiallyUnavailableTimes(partialUnavailable as any);
        }
      }
    } catch (error) {
      console.error("Error fetching unavailable dates:", error);
      toast.error("Failed to load availability data.  Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const onChangeDate = (dates: [Date | null, Date | null]) => {
    const startMoment = moment(dates[0]);
    const endMoment = moment(dates[1]);
    
    // Use operational hours as the preferred default; fall back to company defaults
    const validStartTime =
      startTime && startTime.trim() !== "" ? startTime : opStartTime;
    const validEndTime = endTime && endTime.trim() !== "" ? endTime : opEndTime;

    const isCheck = isRangeAvailable(startMoment, endMoment);
    if (!isCheck) {
      setTempStartDate(null);
      setTempEndDate(null);
      toast.error("Selected range is fully unavailable.");
      return;
    }

    const isPartialValid = isPartialOverlapValid(startMoment, endMoment);
    if (!isPartialValid) {
      setTempStartDate(null);
      setTempEndDate(null);
      toast.error(
        "Selected range contains a partially unavailable date in the middle.",
      );
      return;
    }
    
    // Update temporary state only
    setTempStartDate(dates[0]);
    setTempEndDate(dates[1]);
    
    const today = new Date();
    const visibleEndDate = new Date(today);
    visibleEndDate.setMonth(today.getMonth() + monthsToShow);
    fetchUnavailableDates(today, visibleEndDate);
  };

  useEffect(() => {
    const today = new Date();
    const threeMonthsLater = new Date();
    threeMonthsLater.setMonth(today.getMonth() + monthsToShow);
    fetchUnavailableDates(today, threeMonthsLater);
  }, [selectedCar?.id, monthsToShow, pickupLocation]);

  // Fetch unavailable dates when weeks view month changes
  useEffect(() => {
    if (activeTab === 'weeks' && weeksViewMonth) {
      const monthStart = moment(weeksViewMonth).startOf('month').toDate();
      const monthEnd = moment(weeksViewMonth).endOf('month').toDate();
      fetchUnavailableDates(monthStart, monthEnd);
    }
  }, [weeksViewMonth, activeTab]);

  // Auto-sync date interval when selectedWeeks or times change
  useEffect(() => {
    if (activeTab === 'weeks' && selectedWeeks && selectedWeeks.length > 0 && startTime && endTime) {
      const allWeeks = getAllWeeksForSelection(selectedWeeks, fullyUnavailableDates);
      const range = getSelectedWeeksDateRange(selectedWeeks, allWeeks, startTime, endTime);
      if (range) {
        setStartDate(range.start);
        setEndDate(range.end);
      }
    }
  }, [selectedWeeks, startTime, endTime, fullyUnavailableDates, activeTab]);

  // Adjust times when availability data is loaded and times are blocked
  useEffect(() => {
    if (!startDate || !endDate || Object.keys(partialUnavailability).length === 0) return;
    
    const currentStartTime = startTime && startTime.trim() !== "" ? startTime : opStartTime;
    const currentEndTime = endTime && endTime.trim() !== "" ? endTime : opEndTime;
    
    // Check if current times are blocked for the selected dates
    const startTimeBlocked = isTimeBlocked(startDate, currentStartTime);
    const endTimeBlocked = isTimeBlocked(endDate, currentEndTime);
    
    if (endTimeBlocked) {
      const validEndTime = getAvailableTime(endDate, opEndTime);

      if (validEndTime === null) {
        const use24Hour = ourCompany?.default_time_format === "24_hour";
        const opStart = pickupLocation?.operational_start_time;
        const opEnd = opEndTime;
        toast.error(
          opStart && opEnd
            ? `No available time slot on the selected date. Operational hours: ${formatTime(opStart, use24Hour)} – ${formatTime(opEnd, use24Hour)}.`
            : "No available time slot on the selected date."
        );
        setStartDate(null);
        setEndDate(null);
        return;
      }

      // Update time if it changed
      if (validEndTime !== endTime) {
        setEndTime(validEndTime);
        // Update the date object with the new time
        changeDate([startDate, endDate], setStartDate, setEndDate, currentStartTime, validEndTime);
      }
    }
  }, [partialUnavailability, startDate, endDate]);

  const handleMonthChange = (date: Date) => {
    setCalendarViewDate(date);
    const viewStartDate = new Date(date.getFullYear(), date.getMonth(), 1);
    const viewEndDate = new Date(
      date.getFullYear(),
      date.getMonth() + monthsToShow,
      0,
    );
    fetchUnavailableDates(viewStartDate, viewEndDate);
  };

  // Handle save for months
  const handleMonthSave = () => {
    const startMoment = moment(monthStartDate);
    const endMoment = moment(monthEndDate);

    if (isRangeAvailable(startMoment, endMoment)) {
      // Preserve the user's currently selected times.
      // Only adjust a time if it is blocked by a partial booking on the target date.
      const defaultStartTime = startTime && startTime.trim() !== "" ? startTime : opStartTime;
      const defaultEndTime = endTime && endTime.trim() !== "" ? endTime : opEndTime;

      const validStartTime = (monthStartDate && isTimeBlocked(monthStartDate, defaultStartTime))
        ? getAvailableTime(monthStartDate, opStartTime)
        : defaultStartTime;
      const validEndTime = (monthEndDate && isTimeBlocked(monthEndDate, defaultEndTime))
        ? getAvailableTime(monthEndDate, opEndTime)
        : defaultEndTime;

      if (validStartTime === null || validEndTime === null) {
        const use24Hour = ourCompany?.default_time_format === "24_hour";
        toast.error(
          opStartTime && opEndTime
            ? `No available time slot on the selected date. Operational hours: ${formatTime(opStartTime, use24Hour)} – ${formatTime(opEndTime, use24Hour)}.`
            : "No available time slot on the selected date."
        );
        setStartDate(null);
        setEndDate(null);
        return;
      }

      if (validStartTime !== startTime) setStartTime(validStartTime);
      if (validEndTime !== endTime) setEndTime(validEndTime);

      changeDate(
        [monthStartDate, monthEndDate],
        setStartDate,
        setEndDate,
        validStartTime,
        validEndTime,
      );
      // 👇 Calculate total months (inclusive):
      const totalDays = endMoment.diff(startMoment, "days") + 1;
      const totalMonths = Math.round(totalDays / 30);
      setLongTermRentalDuration(totalMonths);
    } else {
      toast.error("Selected period contains unavailable dates.");
    }
  };

  const handleWeeksSave = () => {
    const allWeeks = getAllWeeksForSelection(selectedWeeks, fullyUnavailableDates);
    const range = getSelectedWeeksDateRange(selectedWeeks, allWeeks, startTime, endTime);
    if (!range) {
      toast.error("Please select at least one week.");
      return;
    }

    const sortedWeeks = [...selectedWeeks].sort();
    const firstWeekKey = sortedWeeks[0];

    const weeksWithUnavailability = allWeeks.filter((week) => {
      const keyDate = week.isCurrentWeek ? week.effectiveStart : week.start;
      const key = moment(keyDate).format("YYYY-MM-DD");
      const isFirstWeek = key === firstWeekKey;

      return (
        selectedWeeks.includes(key) &&
        weekContainsUnavailableDates(
          week,
          fullyUnavailableDates,
          partialUnavailability,
          isFirstWeek,
        )
      );
    });

    if (weeksWithUnavailability.length > 0) {
      toast.error("One or more selected weeks contain unavailable dates.");
      return;
    }

    const startMoment = moment(range.start);
    const endMoment = moment(range.end);

    if (isRangeAvailable(startMoment, endMoment)) {
      // Preserve the user's currently selected times.
      // Only adjust a time if it is blocked by a partial booking on the target date.
      const defaultStartTime = startTime && startTime.trim() !== "" ? startTime : opStartTime;
      const defaultEndTime = endTime && endTime.trim() !== "" ? endTime : opEndTime;

      const validStartTime = (range.start && isTimeBlocked(range.start, defaultStartTime))
        ? getAvailableTime(range.start, opStartTime)
        : defaultStartTime;
      const validEndTime = (range.end && isTimeBlocked(range.end, defaultEndTime))
        ? getAvailableTime(range.end, opEndTime)
        : defaultEndTime;

      if (validStartTime === null || validEndTime === null) {
        const use24Hour = ourCompany?.default_time_format === "24_hour";
        toast.error(
          opStartTime && opEndTime
            ? `No available time slot on the selected date. Operational hours: ${formatTime(opStartTime, use24Hour)} – ${formatTime(opEndTime, use24Hour)}.`
            : "No available time slot on the selected date."
        );
        setStartDate(null);
        setEndDate(null);
        return;
      }

      if (validStartTime !== startTime) setStartTime(validStartTime);
      if (validEndTime !== endTime) setEndTime(validEndTime);

      changeDate(
        [range.start, range.end],
        setStartDate,
        setEndDate,
        validStartTime,
        validEndTime,
      );
      setLongTermRentalDuration(selectedWeeks.length);
    } else {
      toast.error("Selected weeks contain unavailable dates.");
    }
  };

  const isTimeBlocked = (
    date: Date | null | undefined,
    time: string,
  ): boolean => {
    if (!date) return false;
    // Use local partialUnavailability state for immediate accuracy
    const unavailabilityData = partialUnavailability || partiallyUnavailableTimes || {};
    const dateKey = moment(date).format("YYYY-MM-DD");
    const ranges = unavailabilityData[dateKey];
    if (!Array.isArray(ranges) || ranges.length === 0) return false;

    const timeMoment = moment(time, "HH:mm");
    return ranges.some(({ from, to }: { from: string; to: string }) => {
      const fromMoment = moment(from, "HH:mm:ss");
      const toMoment = moment(to, "HH:mm:ss");
      return (
        timeMoment.isSameOrAfter(fromMoment) &&
        timeMoment.isSameOrBefore(toMoment)
      );
    });
  };

  // Helper function to find next available time if default is unavailable.
  // Returns null when every slot within operational hours is also booking-blocked.
  const getAvailableTime = (
    date: Date | null | undefined,
    defaultTime: string,
  ): string | null => {
    if (!date) return defaultTime;

    const isOutsideOp = (t: string): boolean => {
      if (!opStartTime || !opEndTime) return false;
      const tm = moment(t, "HH:mm");
      return tm.isBefore(moment(opStartTime, "HH:mm")) || tm.isAfter(moment(opEndTime, "HH:mm"));
    };

    // If default time is available and within operational hours, use it
    if (!isTimeBlocked(date, defaultTime) && !isOutsideOp(defaultTime)) {
      return defaultTime;
    }

    // Find first available time slot within operational hours
    const slots = Array.from({ length: 24 }, (_, h) => [
      `${h.toString().padStart(2, "0")}:00`,
      `${h.toString().padStart(2, "0")}:30`,
    ]).flat();

    for (const time of slots) {
      if (!isTimeBlocked(date, time) && !isOutsideOp(time)) {
        return time;
      }
    }

    // No slot within operational hours is available
    return null;
  };

  const renderDayContents = (day: number, date?: Date) => {
    if (!date) return day;
    const dateStr = formatDate(date);
    const isPartial = !!partialUnavailability[dateStr];

    return (
      <DatePickerCustomDay
        dayOfMonth={day}
        date={date}
        unavailableDates={fullyUnavailableDates}
        partiallyUnavailable={isPartial}
        partialTimes={partialUnavailability[dateStr]}
      />
    );
  };

  useEffect(() => {
    if (isPanelOpen && window.innerWidth < 768) {
      setTimeout(() => {
        if (panelRef.current) {
          const topPosition = panelRef.current.getBoundingClientRect().top;
          const offset =
            window.pageYOffset || document.documentElement.scrollTop;
          const targetScrollPosition = topPosition + offset - 120;

          window.scrollTo({
            top: targetScrollPosition,
            behavior: "smooth",
          });
        }
      }, 100);
    }
  }, [isPanelOpen]);



  // Render active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case "dates":
        return (
          <DatesView
            startDate={tempStartDate}
            endDate={tempEndDate}
            onChangeDate={onChangeDate}
            monthsToShow={monthsToShow}
            calendarViewDate={calendarViewDate}
            isDateSelectable={isDateSelectable}
            renderDayContents={renderDayContents}
            handleMonthChange={handleMonthChange}
          />
        );
      case "weeks":
        return (
          <WeeksView
            unavailableDates={fullyUnavailableDates}
            partialUnavailability={partialUnavailability}
          />
        );
      case "months":
        return (
          <MonthsView
            dialRef={dialRef}
            isDragging={isDragging}
            handleMouseDown={handleMouseDown}
            handleTouchStart={handleTouchStart}
            openDatePickerModal={openDatePickerModal}
            showDatePicker={showDatePicker}
            tempSelectedDate={tempSelectedDate}
            handleModalDateChange={handleModalDateChange}
            handleModalSave={handleModalSave}
            handleModalClose={handleModalClose}
            monthsToShow={monthsToShow}
            calendarViewDate={calendarViewDate}
            startDate={startDate}
            isDateSelectable={isDateSelectable}
            renderDayContents={renderDayContents}
            handleMonthChange={handleMonthChange}
            isLoading={isLoading}
          />
        );
      default:
        return (
          <DatesView
            startDate={tempStartDate}
            endDate={tempEndDate}
            onChangeDate={onChangeDate}
            monthsToShow={monthsToShow}
            calendarViewDate={calendarViewDate}
            isDateSelectable={isDateSelectable}
            renderDayContents={renderDayContents}
            handleMonthChange={handleMonthChange}
          />
        );
    }
  };

  const handleSave = () => {
    switch (activeTab) {
      case "weeks":
        handleWeeksSave();
        setLongTermRentalNature("weekly");
        setSelectedMonth(1);
        setMonthStartDate(new Date());
        setMonthEndDate(moment().add(29, "days").toDate());
        break;
      case "months":
        handleMonthSave();
        setLongTermRentalNature("monthly");
        setSelectedWeeks([]);
        break;
      case "dates":
      default:
        // Apply temporary dates when saving
        if (tempStartDate && tempEndDate) {
          const startMoment = moment(tempStartDate);
          const endMoment = moment(tempEndDate);

          // Preserve the user's currently selected times.
          // Only adjust a time if it is blocked by a partial booking on the target date.
          const defaultStartTime = startTime && startTime.trim() !== "" ? startTime : opStartTime;
          const defaultEndTime = endTime && endTime.trim() !== "" ? endTime : opEndTime;

          const validStartTime = isTimeBlocked(tempStartDate, defaultStartTime)
            ? getAvailableTime(tempStartDate, opStartTime)
            : defaultStartTime;
          const validEndTime = isTimeBlocked(tempEndDate, defaultEndTime)
            ? getAvailableTime(tempEndDate, opEndTime)
            : defaultEndTime;

          if (validStartTime === null || validEndTime === null) {
            const use24Hour = ourCompany?.default_time_format === "24_hour";
            toast.error(
              opStartTime && opEndTime
                ? `No available time slot on the selected date. Operational hours: ${formatTime(opStartTime, use24Hour)} – ${formatTime(opEndTime, use24Hour)}.`
                : "No available time slot on the selected date."
            );
            setTempStartDate(null);
            setTempEndDate(null);
            return;
          }

          if (validStartTime !== startTime) setStartTime(validStartTime);
          if (validEndTime !== endTime) setEndTime(validEndTime);

          changeDate([tempStartDate, tempEndDate], setStartDate, setEndDate, validStartTime, validEndTime);
          const totalDaysExclusive = endMoment.diff(startMoment, "days") + 1;
          if (totalDaysExclusive) {
            setLongTermRentalDuration(totalDaysExclusive);
          }
        } else if (!startDate || !endDate) {
          toast.error("Please select start and end dates.");
          return;
        }
        setLongTermRentalNature("daily");
        setSelectedWeeks([]);
        setSelectedMonth(1);
        setMonthStartDate(new Date());
        setMonthEndDate(moment().add(29, "days").toDate());
        break;
    }
    setIsPanelOpen(false);
  };

  const use24Hour = ourCompany?.default_time_format === "24_hour";

  return (
    <>
      <div className={`RentalCarDatesRangeInput relative flex ${className}`}>
        <div className={`flex-1 flex items-center focus:outline-none`}>
          {/* Main container with grid layout */}
          <div
            className={`flex-1 flex relative ${fieldClassName} items-center space-x-3 focus:outline-none`}
          >
            <div className="w-full text-left">
              <p className="text-[#1F2937] font-semibold">Until</p>
              <div className="xl:text-lg font-medium text-text-color grid grid-cols-2 gap-2">
                {/* Date Section - Opens Calendar */}
                <button
                  type="button"
                  className="cursor-pointer text-left relative focus:outline-none hover:text-primary-200 transition-colors"
                  onClick={() => {
                    setIsPanelOpen(!isPanelOpen);
                  }}
                >
                  {endDate?.toLocaleDateString("en-US", {
                    month: "short",
                    day: "2-digit",
                  }) || "Date"}
                  {endDate && (
                    <ClearDataButton
                      onClick={() => {
                        setEndDate(null);
                        setPartiallyAvailableEndDate(null);
                        setSelectedWeeks([]);
                        setSelectedMonth(0);
                      }}
                    />
                  )}
                </button>

                <RentalCarTimesRangeInput
                  className="w-full"
                  showTimeType="end"
                  hasButtonSubmit={false}
                />
              </div>
            </div>
          </div>
        </div>

        {isPanelOpen && (
          <div className="h-8 absolute self-center top-1/2 -translate-y-1/2 z-0 -left-0.5 right-1"></div>
        )}

        {isPanelOpen && (
          <div
            ref={panelRef}
            className={`fixed left-1/2 z-[9999] mt-3 top-20 w-screen max-w-96 -translate-x-1/2 ${
              isMiddle ? "lg:-translate-x-1/3" : "-translate-x-1/2"
            } xl:-translate-x-1/2 transform px-4 sm:px-0 md:max-w-2xl`}
            tabIndex={-1}
            onScroll={(e) => e.stopPropagation()}
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <div className="overflow-hidden rounded-2xl shadow-lg ring-1 ring-black/5 bg-white p-8">
              {!bookingDetails && <TabSwitcher />}

              {isLoading ? (
                <div className="pointer-events-none opacity-60 select-none">
                  <div className="relative">{renderTabContent()}</div>
                  <div className="absolute left-0 bottom-0 w-full px-8">
                    <div className="h-1 bg-gray-100 rounded-full overflow-hidden relative">
                      <div className="bar-loader h-full bg-blue-400 rounded-full absolute"></div>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-white opacity-0 pointer-events-auto" />
                </div>
              ) : (
                renderTabContent()
              )}

              <div className="flex justify-end">
                <button
                  className="mt-6 w-[120px] bg-primary-200 hover:bg-primary-700 text-button-color font-semibold rounded-lg py-3 transition-all space-x-2"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSave();
                  }}
                >
                  <span>Save Dates</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Submit button (unchanged logic) */}
      {location.pathname !== "/car-listing-detail" &&
        hasButtonSubmit &&
        location.pathname !== "/listing-car-detail" &&
        design === "designA" && (
          <div className="cursor-pointer min-w-30 mb-0">
            {hasButtonSubmit && (
              <div className="flex justify-right md:pr-4">
                <div
                  onClick={() => {
                    if (pickupLocation === null) {
                      toast.error("Please enter Pick up location");
                    } else if (pickupLocation !== null) {
                      callAvailableFleetAPI(navigate);
                    }
                  }}
                  className={`h-14 md:h-16 w-full lg:w-16 rounded-md bg-primary-200 hover:bg-primary-700 lg:px-5 flex items-center justify-center text-text-color focus:outline-none ${
                    startDate && endDate ? "" : ""
                  }`}
                >
                  {loadingAvailableFleet ? (
                    <svg
                      aria-hidden="true"
                      className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-400"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill"
                      />
                    </svg>
                  ) : (
                    <>
                      <span className="flex lg:hidden text-lg font-bold text-button-color">
                        Search
                      </span>
                      <span className="hidden lg:flex">
                        <div className="text-button-color">
                          <MagnifyingGlassIcon className="w-6 h-6" />
                        </div>
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
    </>
  );
};

export default EndDateTimeRange;
